import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import useScreenSize from '@/hooks/useScreenSize';
import { SearchRandomizedFeedsContext } from '@/pages/search/hooks/searchRandomizedFeeds.context';
import SuarteName from '@/shared-components/icons/components/public/SuarteName';

import AppNavigationOptions from './AppNavigationOptions';
import SuarteworldTransition from './SuarteworldTransition';

import './styles/AppNavigation.css';

const HOME_VIEW_PATH_PREFIXES = ['/galleries', '/artists', '/artworks', '/artlists', '/community'];
const HIDDEN_NAVIGATION_PATHS = ['/payments-profile'];

export default function AppNavigation() {
  const screenSize = useScreenSize();

  const location = useLocation();
  const navigate = useNavigate();
  const { resetFeeds } = useContext(SearchRandomizedFeedsContext);
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [isSuarteworldTransitioning, setIsSuarteworldTransitioning] = useState(false);
  const [suarteworldTransitionPhase, setSuarteworldTransitionPhase] = useState('covering');
  const [suarteworldTransitionRunId, setSuarteworldTransitionRunId] = useState(0);

  useEffect(() => {
    if (location.pathname.startsWith('/suarteworld')) {
      setActiveLink('/suarteworld');
      return;
    }

    const isHomeView = location.pathname === '/' || HOME_VIEW_PATH_PREFIXES.some((prefix) => (
      location.pathname.startsWith(prefix)
    ));

    if (isHomeView) {
      setActiveLink('/');
      return;
    }

    setActiveLink(location.pathname);
  }, [location.pathname]);

  const handleLinkClick = useCallback((path) => {
    if (path === '/search') {
      resetFeeds();
    }
    setActiveLink(path);
  }, [resetFeeds]);

  const handleSuarteworldNavigate = useCallback(() => {
    navigate('/suarteworld');
  }, [navigate]);

  const handleSuarteworldTransitionEnd = useCallback(() => {
    setIsSuarteworldTransitioning(false);
  }, []);

  const handleSuarteworldTransitionFallback = useCallback(() => {
    setSuarteworldTransitionPhase('revealing');
  }, []);

  const handleSuarteworldReady = useCallback(() => {
    if (isSuarteworldTransitioning) {
      setSuarteworldTransitionPhase('revealing');
    }
  }, [isSuarteworldTransitioning]);

  const outletContext = useMemo(() => ({
    onSuarteworldReady: handleSuarteworldReady,
  }), [handleSuarteworldReady]);

  const handleSuarteworldClick = useCallback(() => {
    if (isSuarteworldTransitioning) {
      return;
    }

    if (location.pathname.startsWith('/suarteworld')) {
      handleLinkClick('/suarteworld');
      navigate('/suarteworld', {
        state: location.pathname === '/suarteworld' ? location.state : null,
      });
      return;
    }

    handleLinkClick('/suarteworld');
    setSuarteworldTransitionRunId((previous) => previous + 1);
    setSuarteworldTransitionPhase('covering');
    setIsSuarteworldTransitioning(true);
  }, [handleLinkClick, isSuarteworldTransitioning, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    document.body.classList.toggle('suarteworld-transition-active', isSuarteworldTransitioning);

    return () => {
      document.body.classList.remove('suarteworld-transition-active');
    };
  }, [isSuarteworldTransitioning]);

  const shouldShowNavigation = !HIDDEN_NAVIGATION_PATHS.includes(location.pathname);

  return (
    <>
      <SuarteworldTransition
        active={isSuarteworldTransitioning}
        phase={suarteworldTransitionPhase}
        runId={suarteworldTransitionRunId}
        onNavigate={handleSuarteworldNavigate}
        onFallback={handleSuarteworldTransitionFallback}
        onExitComplete={handleSuarteworldTransitionEnd}
      />
      <div className="app-navigation__page">
        {shouldShowNavigation && (
          <nav className="app-navigation__container-outter">
            <div className="app-navigation__container">
              {screenSize.width > 650 && (
                <Link
                  className="app-navigation__suarte-name-link"
                  to="/"
                  aria-label="Home"
                  onClick={() => handleLinkClick('/')}
                >
                  <SuarteName />
                </Link>
              )}
              <AppNavigationOptions
                activeLink={activeLink}
                handleLinkClick={handleLinkClick}
                onSuarteworldClick={handleSuarteworldClick}
              />
            </div>
          </nav>
        )}

        <Outlet context={outletContext} />
      </div>
    </>
  );
}
